import { useLayoutEffect, useRef } from 'react';

/** What a ready step's control does: rip the pack, spin the carousel's ring, or fast-forward the lane. */
export type ReadyActionId = 'rip' | 'shuffle' | 'skip';

/**
 * A ready step's own control — the slot lane's RIP! or Skip, a library scene's RIP! or Shuffle — for a host that
 * draws it somewhere else.
 */
export interface ReadyAction {
  id: ReadyActionId;
  /** The label the step's own button carries. */
  label: string;
  /** Presses the step's own button: its latest handler, and nothing while the action is disabled. */
  run: () => void;
  /** The step's own button would be disabled. */
  disabled: boolean;
}

/** Hears a step's controls whenever one of them changes, and `[]` once there are none or the step goes. */
export type ReadyActionsListener = (actions: ReadyAction[]) => void;

/** No control to offer — one array, so a host can tell nothing changed. */
export const NO_READY_ACTIONS: ReadyAction[] = [];

const signatureOf = (actions: ReadyAction[]) =>
  actions.map(({ id, label, disabled }) => `${id}:${label}:${disabled ? 'off' : 'on'}`).join('|');

/**
 * Hands a step's controls to the host that draws them (`listener`), before the browser paints: on mount, whenever an
 * action, its label or its state changes — not on every render, so a host re-rendering on the report is not told
 * again — and `[]` on unmount. What the host is handed calls the step's handler of its latest render, so a press
 * never acts on a state gone by. Without a listener it does nothing: the step draws its own buttons.
 */
export function useReportedActions(listener: ReadyActionsListener | undefined, actions: ReadyAction[]): void {
  const latest = useRef({ listener, actions });
  latest.current = { listener, actions };
  const signature = signatureOf(actions);
  const lifted = listener !== undefined;

  useLayoutEffect(() => {
    if (!lifted) return;
    const press = (id: ReadyActionId) => () => {
      const action = latest.current.actions.find((one) => one.id === id);
      if (action && !action.disabled) action.run();
    };
    latest.current.listener?.(
      latest.current.actions.map(({ id, label, disabled }) => ({ id, label, disabled, run: press(id) })),
    );
  }, [lifted, signature]);

  useLayoutEffect(() => {
    const current = latest;
    return () => current.current.listener?.(NO_READY_ACTIONS);
  }, []);
}
